import React, { useContext, useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

// Importing custom components
import CustomInput from '../widgets/CustomInput'; // Custom input field component
import CustomBtn from '../widgets/CustomBtn'; // Custom button component
import CustomModal from '../widgets/CustomModal';
import ImageCropper from '../widgets/ImageCropper';

import AppContext from '../context/AppContext'; // Application context for shared state

import { useDispatch, useSelector } from 'react-redux'; // Redux hooks for dispatching actions and selecting state
import { signUp, addImage } from '../_slice/onboardingSlice'; // Actions from onboarding slice

// Importing image assets
import logoImg from "../../assets/images/lociLogo.png";
import closeEye from '../../assets/images/close_eye.png';
import openEye from '../../assets/images/open_eye.png';
import uploadImg from "../../assets/images/uploadimg.png"
import rightTick from "../../assets/images/tickRight.png";
import crosstick from "../../assets/images/crosssIcon.png"
import editIcon from "../../assets/images/editIcon.png"
/**
 * SignUp component for user registration.
 * @component
 */
function SignUp() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext); // Context to set the loader state

  const fileInputRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const [imgCropModal, setImgCropModal] = useState({ show: false })
  const [croppedImage, setCroppedImage] = useState(null);

  // State to manage password field focus and visibility
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [showPassword, setShowPassword] = useState({
    password: false,
  });

  // State to manage file upload
  const [file, setFile] = useState({
    viewImg: null,
    uploadedImg: null
  });

  // State to manage logo validation error
  const [logoError, setLogoError] = useState('');

  // Setting up form handling with react-hook-form
  const { register, handleSubmit, formState: { errors }, watch } = useForm({
    mode: 'onChange', // Validate on change event
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      phone: '',
      organisation: '',
      website: ''
    }
  });

  // Extracting imgData key from Redux state
  const imgDataKey = useSelector(state => state?.onboarding?.imgData?.key);
  const watchPassword = watch('password', '');

  // Update the file state when imgDataKey changes
  useEffect(() => {
    if (imgDataKey) {
      setFile({ ...file, uploadedImg: imgDataKey });
    }
  }, [imgDataKey]);

  /**
   * Handle form submission.
   * @param {Object} data - Form data containing first_name, last_name, email, password, phone, and organisation.
   */
  const onSubmit = (data) => {
    if (!file.viewImg) {
      setLogoError('Logo required');
      return;
    }
    if (!isPasswordValid(watchPassword)) {
      setIsPasswordFocused(true);
      return;
    }

    const sanitizedData = { ...data };

    // Phone is optional: drop the key entirely when blank so the API does not
    // treat an empty value as a real (and therefore duplicate) phone number.
    if (!sanitizedData.phone || sanitizedData.phone.trim() === '') {
      delete sanitizedData.phone;
    } else {
      sanitizedData.phone = sanitizedData.phone.trim();
    }

    dispatch(
      signUp({
        initialPost: { ...sanitizedData, logo: file?.uploadedImg },
        navigate,
        navigatePath: '/verification',
        state: { email: sanitizedData.email, type: 'signup' },
        setLoader
      })
    );
  };

  // Callback function to update file state
  // const handleFileChange = (selectedFile) => {
  //   setFile({ ...file, viewImg: selectedFile });
  //   setLogoError(''); // Clear logo error when a file is selected
  //   const formData = new FormData();
  //   formData.append('image', selectedFile);
  //   formData.append('type', 'org_logo');
  //   dispatch(addImage({ formData, setLoader }));
  // };

  const handleFileChange = (selectedFile) => {
    setFile({ ...file, viewImg: URL.createObjectURL(selectedFile) });
    setImgCropModal({ show: true }); // Show the cropper modal
    setLogoError('');
  };

  const handleCropComplete = (croppedImageBlob) => {
    const croppedImageUrl = URL.createObjectURL(croppedImageBlob); // Create URL for the cropped image
    setCroppedImage(croppedImageBlob);

    // Set the cropped image as the viewImg
    setFile(prevFile => ({ ...prevFile, viewImg: croppedImageUrl }));

    const formData = new FormData();
    formData.append('image', croppedImageBlob);
    formData.append('type', 'org_logo');
    dispatch(addImage({ formData, setLoader }));

    setImgCropModal({ show: false });
    setLogoError(false);
  };

  // Handler to ensure phone number input contains only digits
  const handleInput = (event) => {
    event.target.value = event.target.value.replace(/[^0-9]/g, '');
  };

  // Function to validate password strength
  const isPasswordValid = (password) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+{}|\\[\]:";'<>?,./~`])[A-Za-z\d!@#$%^&*()\-_=+{}|\\[\]:";'<>?,./~`]{12,}$/.test(password);

  // Function to determine color based on password validity
  const getColor = (password) => (password === '' ? '' : isPasswordValid(password) ? '#292929' : 'red');

  // Function to generate validation message for password rules
  const getValidationMessage = (regex, message, password) => {
    const isValid = regex.test(password);
    const icon = regex.test(password) ? <img src={rightTick} alt='' /> : <img src={crosstick} alt='' />;
    const space = '\u00A0\u00A0';
    const colorStyle = isValid ? { color: '#05C680' } : { color: getColor(password) };

    return (
      <div style={{ ...colorStyle, marginTop: "15px" }} >
        {icon} {space} {message}
      </div>
    );
  };

  const handleEditClick = () => {
    fileInputRef.current.click();
  };

  // Check if all password conditions are met
  const allConditionsMet = isPasswordValid(watch('password'));

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Main container for SignUp component */}
        <div className='onboarding-box'>
          <div className="onboarding-box-header">
            <img src={logoImg} alt='Logo' className='onboarding-box-header-img' />
            <p className='onboarding-box-header-text'>The Evolution of Safety.</p>
          </div>
          <h2 className='onboardHeading'>CREATE ACCOUNT</h2>
          <div className='row'>
            {/* First Name input */}
            <div className="col-lg-6">
              <CustomInput
                label="First Name"
                type="text"
                classname="custom_input"
                tabIndex="1"
                {...register("first_name", {
                  required: 'First name required'
                })}
              />
              {errors.first_name && <p className='text-danger'>{errors.first_name.message}</p>}
            </div>
            {/* Last Name input */}
            <div className="col-lg-6">
              <CustomInput
                label="Last Name"
                type="text"
                classname="custom_input"
                tabIndex="1"
                {...register("last_name", {
                  required: 'Last name required'
                })}
              />
              {errors.last_name && <p className='text-danger'>{errors.last_name.message}</p>}
            </div>
            {/* Email input */}
            <div className="col-lg-6">
              <CustomInput
                label="Email"
                labelText="MFA Required"
                type="email"
                classname="custom_input"
                tabIndex="1"
                {...register("email", {
                  required: 'Email required',
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/i,
                    message: 'Invalid email address'
                  }
                })}
              />
              {errors.email && <p className='text-danger'>{errors.email.message}</p>}
            </div>
            {/* Phone Number input */}
            <div className="col-lg-6">
              <CustomInput
                label="Phone Number"
                labelText="Optional"
                type="text"
                classname="custom_input"
                tabIndex="1"
                onInput={handleInput}
                {...register("phone", {
                  // Optional field: an empty value is always accepted.
                  validate: (value) =>
                    !value || value.trim() === ''
                      ? true
                      : /^[0-9]{10}$/.test(value.trim()) ||
                        'Phone number must be exactly 10 digits'
                })}
              />
              {errors.phone && <p className='text-danger'>{errors.phone.message}</p>}
            </div>
            {/* Password input */}
            <div className="col-lg-6">
              <CustomInput
                label="Password"
                type={showPassword.password ? 'text' : 'password'}
                icon={true}
                src={showPassword.password ? openEye : closeEye}
                onClick={() =>
                  setShowPassword((prev) => ({
                    ...prev,
                    password: !prev.password,
                  }))
                }
                classname="custom_input padding_right"
                tabIndex="1"
                {...register("password", {
                  required: 'Password required',
                  // pattern: {
                  //   value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@!_%&*]).{14,}$/,
                  //   message: 'Password must have at least 14 characters, having uppercase, lowercase letters, digits and special characters'
                  // }
                })}
                onFocus={() => setIsPasswordFocused(true)}
                onBlur={() => setIsPasswordFocused(false)}
              />

              {/* Display password strength rules */}
              {isPasswordFocused && !allConditionsMet && (
                <div style={{ color: getColor(watchPassword) }} className='password_strengthbox'>
                  <b className='password_heading mb-4'>Password must be</b>
                  {getValidationMessage(/.{12,}$/, ' Minimum 12 characters', watchPassword)}
                  {getValidationMessage(/(?=.*[a-z])/, ' At least one lowercase letter', watchPassword)}
                  {getValidationMessage(/(?=.*[A-Z])/, ' At least one uppercase letter', watchPassword)}
                  {getValidationMessage(/(?=.*\d)/, ' At least one number', watchPassword)}
                  {getValidationMessage(/[!@#$%^&*()\-_=+{}|\\[\]:";'<>?,./~`]/, 'At least one special character', watchPassword)}
                </div>
              )}
              {errors.password && <p className='text-danger'>{errors.password.message}</p>}
            </div>
            {/* Organization input */}
            <div className="col-lg-6 ">
              <div className='position-relative'>
                <CustomInput
                  label="Organization"
                  type="text"
                  classname="custom_input"
                  tabIndex="1"
                  {...register("organisation", {
                    required: 'Organization required'
                  })}
                />
              </div>
              {errors.organisation && <p className='text-danger'>{errors.organisation.message}</p>}
            </div>
            {/* Website input */}
            <div className="col-lg-6">
              <div className="position-relative">
                <CustomInput
                  label="Enter your Company's website URL or the link to your Safety landing page"
                  type="text"
                  classname="custom_input"
                  placeholder="http://www.learnbyloci.com"
                  tabIndex="1"
                  {...register("website", {
                    // required: 'Website URL is required',
                  })}
                />
              </div>
              {/* {errors.website && <p className="text-danger">{errors.website.message}</p>} */}
            </div>
            <div className='col-lg-6'></div>
            <div className="col-lg-6 position-relative">
              <label htmlFor="" className='custom_lable mt-5'> Upload your company logo</label>
              {/* File upload section */}
              <div className='extra_section mt-6'>
                <div className={`img_upload_section_signup ${file?.viewImg ? "dynHeight" : ""}`}>
                  <div
                    className={`upload_sign_signup ${file.viewImg ? "cusPadding" : ""}`}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    <input
                      type="file"
                      name="image"
                      className="img_upload_section_file_signup"
                      // accept="image/*"
                      ref={fileInputRef}
                      accept="image/png, image/jpeg, image/jpg"
                      onChange={(e) => handleFileChange(e.target.files[0])}
                    />
                    {/* Display uploaded image or upload prompt */}
                    {file?.viewImg ? (
                      // <img src={URL.createObjectURL(file?.viewImg)} alt="Uploaded" className="upload_sign_img_view" />
                      <div className="image-container">
                        <img src={file?.viewImg} alt="Uploaded" className="upload_sign_img_view" />
                        {isHovered && (
                          <div className="overlay" title='click on edit icon to upload image again'>
                            <img src={editIcon} alt='' onClick={handleEditClick} className='edit-icon' />
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <img src={uploadImg} alt="Upload" className="upload_sign_img" />
                        <span className='upload_sign_text'>Upload Logo</span>
                      </>
                    )}
                  </div>
                </div>
                {logoError && <p className='text-danger mt-2'>{logoError}</p>}
              </div>
            </div>
          </div>
          <div className='d-flex align-items-center justify-content-end custom-margintop'>
            <button type='button' className='cancelBtn' onClick={() => navigate(-1)}>CANCEL</button>
            <CustomBtn
              buttonText="Sign Up"
              type="submit"
              id="signupbtn"
            />
          </div>
        </div>
      </form>

      {/* Cropper modal */}
      <CustomModal
        show={imgCropModal.show}
        onHide={() => setImgCropModal({ show: false })}
        heading="Upload Logo"
        size="md"
        body={
          <div className='deleteModal'>
            <div className='d-flex justify-content-center align-items-center custom_bottom_padding'>
              <ImageCropper
                image={file.viewImg}  // Pass the selected image to the cropper
                onCropComplete={handleCropComplete}
                width={200} // Define the crop box width
                height={100} // Define the crop box height
                padding={50} // Default padding for the crop box
              />
            </div>
          </div>
        }
      />

    </>
  );
}

export default SignUp;